import {
  faCheckCircle,
  faCircleXmark,
  faClone,
  faComment,
  faPencil,
  faPlus,
  faStopwatch,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { DataTable } from "@ventumsoftware/react-toolkit";
import moment from "moment";
import { type FC, type ReactNode, useEffect, useRef } from "react";
import {
  type InspectionComment,
  type InspectionFinding,
  type InspectionSeverity,
  inspectionSeverityOptions,
} from "../../../api/dto/InspectionFinding";
import type { Question, QuestionAnswerValue } from "../../../api/dto/Question";
import { AddInspectionFindingModal } from "../../../modals/AddInspectionFindingModal";
import { DeleteInspectionFindingModal } from "../../../modals/DeleteInspectionFindingModal";
import { EditInspectionFindingModal } from "../../../modals/EditInspectionFindingModal";
import { Badge, BadgeStyle } from "../../data/Badge";
import { Button, IconButton } from "../../form/Button";
import { useModal } from "../../layout/ModalProvider";
import { InspectionFindingSeverityBadge } from "../InspectionFindingSeverityBadge";

export type AvailableSeverities = {
  question_id: string;
  question_number: string | null;
  available_severities: InspectionSeverity[];
}[];

/** A staged (not-yet-persisted) create/update/delete */
export type FindingChange =
  | {
      type: "upsert";
      id: string;
      questionId: string;
      severity: InspectionSeverity;
      description: string;
      comments: InspectionComment[];
    }
  | { type: "delete"; id: string; questionId: string };

export const QuestionFindingCell: FC<{
  question: Question;
  /** Current (possibly unsaved) answer - used to resolve the selected option's severities. */
  answer: QuestionAnswerValue;
  /**
   * A question has at most one finding; more than one means duplicates were created (e.g. a
   * finding added by hand next to one the backend generated for the answer), which the user
   * should be able to see and clean up here.
   */
  findings: InspectionFinding[];
  availableSeverities: AvailableSeverities;
  onStage: (change: FindingChange) => void;
}> = (props) => {
  // Prefer the option's own allowed severities. The backend's
  // `availableSeverities` is only avaiable for already saved answeres.
  const selectedOption =
    props.question.type === "multi_question" && typeof props.answer === "number"
      ? (props.question.options?.[props.answer] ?? null)
      : null;

  const serverAvailableSeverities = props.availableSeverities.find(
    (av) => av.question_id === props.question.id,
  )?.available_severities;

  const availableSeverities: InspectionSeverity[] = selectedOption?.allowedFindingSeverities.length
    ? selectedOption.allowedFindingSeverities
    : serverAvailableSeverities?.length
      ? serverAvailableSeverities
      : (Object.keys(inspectionSeverityOptions) as InspectionSeverity[]);

  // Only multi-choice questions support findings, and only once answered.
  const canAddFinding = props.question.type === "multi_question" && selectedOption !== null;

  const addFindingModal = useModal({
    title: "Bevinding toevoegen",
    body: (
      <AddInspectionFindingModal
        questionId={props.question.id}
        availableSeverities={availableSeverities}
        onStage={({ id, questionId, severity, description }) =>
          props.onStage({ type: "upsert", id, questionId, severity, description, comments: [] })
        }
      />
    ),
  });

  // One modal per action, opened with the id of the finding it applies to.
  const findingById = (params?: string[]) =>
    props.findings.find((finding) => finding.id === params?.[0]) ?? null;

  const editFindingModal = useModal({
    title: "Bevinding bewerken",
    size: "xl",
    body: (params) => {
      const finding = findingById(params);
      return finding ? (
        <EditInspectionFindingModal
          finding={finding}
          availableSeverities={availableSeverities}
          onStage={({ id, questionId, severity, description, comments }) =>
            props.onStage({ type: "upsert", id, questionId, severity, description, comments })
          }
        />
      ) : null;
    },
  });

  const deleteFindingModal = useModal({
    title: "Bevinding verwijderen",
    body: (params) => {
      const finding = findingById(params);
      return finding ? (
        <DeleteInspectionFindingModal
          onStage={() =>
            props.onStage({ type: "delete", id: finding.id, questionId: props.question.id })
          }
        />
      ) : null;
    },
  });

  // If the answer changes after a finding was already added, the finding's
  // classification may no longer be valid for the new answer - open the edit
  // modal (which defaults to a valid severity). Only for the first stale finding,
  // so a single answer change never stacks several modals.
  const staleFinding =
    props.findings.find((finding) => !availableSeverities.includes(finding.severity)) ?? null;
  const promptedAnswerRef = useRef<QuestionAnswerValue | undefined>(undefined);
  useEffect(() => {
    if (!staleFinding || promptedAnswerRef.current === props.answer) {
      return;
    }
    promptedAnswerRef.current = props.answer;
    editFindingModal.open([staleFinding.id]);
  }, [staleFinding, props.answer, editFindingModal]);

  if (props.findings.length === 0) {
    if (!canAddFinding) {
      return null;
    }
    return (
      <div className={"mt-3"}>
        <Button
          type={"secondary"}
          size={"sm"}
          icon={faPlus}
          text={"Bevinding"}
          onClick={() => addFindingModal.open()}
        />
      </div>
    );
  }

  const hasDuplicates = props.findings.length > 1;

  return (
    <div className={"mt-3"}>
      {hasDuplicates && (
        <div
          className={
            "mb-2 flex items-start rounded-sm border border-yellow-300 bg-yellow-50 px-3 py-2 text-sm text-yellow-800"
          }
        >
          <FontAwesomeIcon icon={faClone} className={"mr-2 mt-0.5"} />
          <div>
            Deze vraag heeft {props.findings.length} bevindingen, terwijl er één hoort te zijn.
            Verwijder de dubbele bevinding(en) en houd de juiste over.
          </div>
        </div>
      )}
      <DataTable
        keyProperty={"id"}
        data={props.findings}
        compact
        pageSize={0}
        columns={[
          {
            property: "severity",
            header: "Klassificatie",
            transform: (severity) => (
              <div className={"flex flex-wrap items-center gap-1"}>
                <InspectionFindingSeverityBadge severity={severity} />
                {hasDuplicates && (
                  <Badge style={BadgeStyle.warning}>
                    <FontAwesomeIcon icon={faClone} className={"mr-1"} />
                    Dubbel
                  </Badge>
                )}
              </div>
            ),
          },
          {
            property: "description",
            header: "Toelichting",
            transform: (desc) =>
              desc ? (
                <span className={"text-slate-700 whitespace-pre-wrap"}>{desc}</span>
              ) : (
                <span className={"text-slate-400 italic"}>Geen toelichting</span>
              ),
          },
          {
            property: "id",
            header: "Status",
            transform: (_, row) => findingStatus(row),
          },
          {
            property: "id",
            header: "",
            transform: (_, row) => (
              <div className={"flex items-center justify-end"}>
                <IconButton
                  icon={faPencil}
                  size={"md"}
                  type={"secondary"}
                  onClick={() => editFindingModal.open([row.id])}
                />
                <IconButton
                  icon={faTimes}
                  size={"md"}
                  type={"danger"}
                  onClick={() => deleteFindingModal.open([row.id])}
                />
              </div>
            ),
          },
        ]}
      />
    </div>
  );
};

const findingStatus = (finding: InspectionFinding): ReactNode => {
  const fixedComment = finding.comments
    .filter((c) => c.isFixedAt !== null)
    .reduce(
      (earliest, c) => (earliest === null || c.isFixedAt! < earliest.isFixedAt! ? c : earliest),
      null as (typeof finding.comments)[number] | null,
    );

  if (fixedComment) {
    return (
      <div
        className={`flex items-center text-sm ${fixedComment.isFixedWithin24Hours ? "text-green-700" : "text-red-700"}`}
      >
        <FontAwesomeIcon
          icon={fixedComment.isFixedWithin24Hours ? faCheckCircle : faComment}
          className={"mr-1"}
        />
        <div>{moment(fixedComment.isFixedAt).format("ddd, hA")}</div>
      </div>
    );
  }

  if (finding.severity !== "critical" && finding.severity !== "lsr_violation") {
    return null;
  }

  if (finding.canStillBeFixed) {
    return (
      <div className={"flex items-center text-sm"}>
        <FontAwesomeIcon icon={faStopwatch} className={"mr-1 text-blue-700"} /> Open
      </div>
    );
  }

  return (
    <div className={"flex items-center text-sm text-red-700"}>
      <FontAwesomeIcon icon={faCircleXmark} className={"mr-1"} /> Verlopen
    </div>
  );
};
